import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { brand, brandCopyright } from '@/config/brand';
import { AuthShell } from './components/AuthShell';
import { authBrandPanel } from './components/authBrandPanel';
import { LoginForm } from './components/LoginForm';
import loginStyles from './components/LoginForm.module.css';
import { EmailVerificationForm } from './components/EmailVerificationForm';
import { TotpVerificationForm } from './components/TotpVerificationForm';
import { returnPath, returnState } from './return-to';

type Step =
  | { kind: 'login' }
  | { kind: 'email'; email: string; uncompleted: boolean }
  | { kind: 'totp'; email: string; password: string };

export default function LoginPage() {
  const signupState = returnState(returnPath(useLocation().state));
  const [step, setStep] = useState<Step>({ kind: 'login' });
  const backToLogin = () => setStep({ kind: 'login' });

  if (step.kind === 'totp') {
    return (
      <AuthShell
        title="Two-factor authentication"
        subtitle="Enter the code from your authenticator app."
        brandPanel={authBrandPanel()}
      >
        <TotpVerificationForm
          email={step.email}
          password={step.password}
          onBack={backToLogin}
        />
      </AuthShell>
    );
  }

  if (step.kind === 'email') {
    return (
      <AuthShell
        title="Verify your email"
        subtitle="One more step to continue."
        brandPanel={authBrandPanel()}
      >
        <EmailVerificationForm
          email={step.email}
          uncompleted={step.uncompleted}
          onBack={backToLogin}
        />
      </AuthShell>
    );
  }

  const subtitle = brand.features.sso
    ? `Enter your email and we'll detect the right sign-in method for your account. Existing accounts will be guided to upgrade to ${brand.ssoName} single sign-on.`
    : 'Enter your email and password to sign in to your account.';

  return (
    <AuthShell
      title={`Sign in to ${brand.shortName}`}
      subtitle={subtitle}
      compact
      footer={<span className={loginStyles.copyright}>{brandCopyright()}</span>}
      brandPanel={authBrandPanel()}
      headerEnd={
        <>
          New to {brand.shortName}?
          <Link to="/auth/register" state={signupState}>Sign Up</Link>
        </>
      }
    >
      <LoginForm
        onEmailMfa={(email, uncompleted) =>
          setStep({ kind: 'email', email, uncompleted })
        }
        onTotpMfa={(email, password) => setStep({ kind: 'totp', email, password })}
      />
    </AuthShell>
  );
}
